import type { AdsConfig, RewardedPlacement } from '@/api/types';
import type { ConsentState } from './consent';
import type { AdProvider, CardPlacement, DisplayHandle, InterstitialResult, RewardedResult } from './types';

/** Google Publisher Tag for Google Ad Manager (SPEC_V5 §1). */
export const GPT_SRC = 'https://securepubads.g.doubleclick.net/tag/js/gpt.js';

// ---- the small part of the GPT API we use (no extra dependency)
export interface GptSlot {
  addService(s: GptPubAds): GptSlot;
  getSlotElementId(): string;
}
interface GptEvent {
  slot: GptSlot;
  isEmpty?: boolean;
  makeRewardedVisible?: () => void;
  makeGameManualInterstitialVisible?: () => void;
}
type GptListener = (e: GptEvent) => void;
export interface GptPubAds {
  addEventListener(type: string, l: GptListener): GptPubAds;
  removeEventListener(type: string, l: GptListener): boolean;
  setPrivacySettings(s: { nonPersonalizedAds?: boolean; restrictDataProcessing?: boolean; limitedAds?: boolean }): GptPubAds;
  collapseEmptyDivs(collapseBeforeAdFetch?: boolean): boolean;
}
export interface Googletag {
  cmd: Array<() => void> | { push(f: () => void): number };
  apiReady?: boolean;
  enums: { OutOfPageFormat: Record<string, number | undefined> };
  pubads(): GptPubAds;
  defineSlot(path: string, size: unknown, divId: string): GptSlot | null;
  defineOutOfPageSlot(path: string, format: number): GptSlot | null;
  enableServices(): void;
  display(slotOrDiv: GptSlot | string): void;
  destroySlots(slots?: GptSlot[]): boolean;
}
type GptWindow = { googletag?: Googletag };

let loading: Promise<Googletag> | null = null;

/**
 * Inject gpt.js once. Callers must already have checked every gate (ads on,
 * eligible user, not an excluded screen, consent) — see runtime.ts.
 */
export function loadGpt(timeoutMs = 8000): Promise<Googletag> {
  if (loading) return loading;
  loading = new Promise<Googletag>((resolve, reject) => {
    const w = window as unknown as GptWindow;
    w.googletag = w.googletag || ({ cmd: [] } as unknown as Googletag);
    const fail = (why: string) => {
      clearTimeout(t);
      loading = null;
      reject(new Error(why));
    };
    const t = setTimeout(() => fail('gpt timeout'), timeoutMs);
    let s = document.querySelector<HTMLScriptElement>(`script[src="${GPT_SRC}"]`);
    if (!s) {
      s = document.createElement('script');
      s.async = true;
      s.src = GPT_SRC;
      s.crossOrigin = 'anonymous';
      s.dataset.popinAds = 'gpt';
      document.head.appendChild(s);
    }
    s.addEventListener('error', () => fail('gpt blocked'));
    s.addEventListener('load', () => {
      const gt = w.googletag!;
      gt.cmd.push(() => {
        clearTimeout(t);
        resolve(gt);
      });
    });
  });
  return loading;
}

const DISPLAY_SIZES: unknown = [[300, 250], [320, 100], [320, 50], 'fluid'];
let divSeq = 0;

export class GptAdProvider implements AdProvider {
  readonly kind = 'gpt' as const;
  private slots = new Set<GptSlot>();
  private configured = false;

  constructor(
    private readonly gt: Googletag,
    private readonly ads: AdsConfig,
    private readonly consent: ConsentState,
  ) {}

  private run(f: () => void) {
    this.gt.cmd.push(() => {
      if (!this.configured) {
        this.configured = true;
        const pub = this.gt.pubads();
        // No consent signal for personalisation ⇒ non-personalised ads (SPEC_V5 §0.5).
        pub.setPrivacySettings({ nonPersonalizedAds: !this.consent.personalized, restrictDataProcessing: this.consent.restrictDataProcessing });
        pub.collapseEmptyDivs(true);
      }
      f();
    });
  }

  private listen(handlers: Record<string, GptListener>): () => void {
    const pub = this.gt.pubads();
    for (const [k, h] of Object.entries(handlers)) pub.addEventListener(k, h);
    return () => {
      for (const [k, h] of Object.entries(handlers)) {
        try {
          pub.removeEventListener(k, h);
        } catch {
          /* older GPT */
        }
      }
    };
  }

  private destroy(slot: GptSlot | null) {
    if (!slot) return;
    this.slots.delete(slot);
    try {
      this.gt.destroySlots([slot]);
    } catch {
      /* ignore */
    }
  }

  showRewarded(_placement: RewardedPlacement): Promise<RewardedResult> {
    const unit = this.ads.units.rewarded;
    if (!unit) return Promise.resolve('unavailable');
    return new Promise((resolve) => {
      let slot: GptSlot | null = null;
      let granted = false;
      let done = false;
      let off = () => undefined as void;
      const readyTimer = setTimeout(() => finish('unavailable'), 10_000);
      const finish = (r: RewardedResult) => {
        if (done) return;
        done = true;
        clearTimeout(readyTimer);
        off();
        this.destroy(slot);
        resolve(r);
      };
      this.run(() => {
        const fmt = this.gt.enums.OutOfPageFormat.REWARDED;
        slot = fmt === undefined ? null : this.gt.defineOutOfPageSlot(unit, fmt);
        if (!slot) return finish('unavailable'); // format unsupported on this page/device
        slot.addService(this.gt.pubads());
        this.slots.add(slot);
        const mine = (e: GptEvent) => e.slot === slot;
        off = this.listen({
          rewardedSlotReady: (e) => {
            if (!mine(e)) return;
            clearTimeout(readyTimer);
            e.makeRewardedVisible?.();
          },
          rewardedSlotGranted: (e) => {
            if (mine(e)) granted = true;
          },
          rewardedSlotClosed: (e) => {
            if (mine(e)) finish(granted ? 'granted' : 'closed');
          },
          slotRenderEnded: (e) => {
            if (mine(e) && e.isEmpty) finish('unavailable');
          },
        });
        this.gt.enableServices();
        this.gt.display(slot);
      });
    });
  }

  /**
   * GPT's web INTERSTITIAL format only appears on GPT's own navigation
   * triggers (link clicks, tab switches), which a single-page app's "Next
   * person" never fires. The programmatic out-of-page interstitial
   * (`GAME_MANUAL_INTERSTITIAL`: ready → make visible → closed) is the GPT
   * format that can be shown at a moment we choose. When the network doesn't
   * have it, `defineOutOfPageSlot` returns null and we report 'unavailable' —
   * the caller simply navigates.
   */
  showInterstitial(_placement: 'between_calls', opts: { loadTimeoutMs?: number } = {}): Promise<InterstitialResult> {
    const unit = this.ads.units.interstitial;
    if (!unit) return Promise.resolve('unavailable');
    return new Promise((resolve) => {
      let slot: GptSlot | null = null;
      let done = false;
      let off = () => undefined as void;
      const readyTimer = setTimeout(() => finish('unavailable'), opts.loadTimeoutMs ?? 5000);
      const finish = (r: InterstitialResult) => {
        if (done) return;
        done = true;
        clearTimeout(readyTimer);
        off();
        this.destroy(slot);
        resolve(r);
      };
      this.run(() => {
        const fmt = this.gt.enums.OutOfPageFormat.GAME_MANUAL_INTERSTITIAL;
        slot = fmt === undefined ? null : this.gt.defineOutOfPageSlot(unit, fmt);
        if (!slot) return finish('unavailable');
        slot.addService(this.gt.pubads());
        this.slots.add(slot);
        const mine = (e: GptEvent) => e.slot === slot;
        let visible = false;
        off = this.listen({
          gameManualInterstitialSlotReady: (e) => {
            if (!mine(e) || done) return;
            clearTimeout(readyTimer);
            visible = true;
            e.makeGameManualInterstitialVisible?.();
          },
          gameManualInterstitialSlotClosed: (e) => {
            if (mine(e)) finish(visible ? 'shown' : 'unavailable');
          },
          slotRenderEnded: (e) => {
            if (mine(e) && e.isEmpty) finish('unavailable');
          },
        });
        this.gt.enableServices();
        this.gt.display(slot);
      });
    });
  }

  renderDisplay(placement: CardPlacement, el: HTMLElement): Promise<DisplayHandle> {
    const unit = this.ads.units[placement];
    const empty: DisplayHandle = { filled: false, destroy: () => undefined };
    if (!unit) return Promise.resolve(empty);
    if (!el.id) el.id = `popin-ad-${placement}-${++divSeq}`;
    return new Promise((resolve) => {
      let slot: GptSlot | null = null;
      let settled = false;
      let off = () => undefined as void;
      const handle = (filled: boolean): DisplayHandle => ({
        filled,
        destroy: () => {
          off();
          this.destroy(slot);
        },
      });
      const settle = (filled: boolean) => {
        if (settled) return;
        settled = true;
        clearTimeout(t);
        resolve(handle(filled));
      };
      const t = setTimeout(() => settle(false), 8000);
      this.run(() => {
        slot = this.gt.defineSlot(unit, DISPLAY_SIZES, el.id);
        if (!slot) return settle(false);
        slot.addService(this.gt.pubads());
        this.slots.add(slot);
        off = this.listen({
          slotRenderEnded: (e) => {
            if (e.slot === slot) settle(!e.isEmpty);
          },
        });
        this.gt.enableServices();
        this.gt.display(el.id);
      });
    });
  }

  destroyAll(): void {
    const all = [...this.slots];
    this.slots.clear();
    if (!all.length) return;
    this.gt.cmd.push(() => {
      try {
        this.gt.destroySlots(all);
      } catch {
        /* ignore */
      }
    });
  }
}

/** Tests only. */
export function __resetGpt(): void {
  loading = null;
}
